import styled from '@emotion/styled';
import { ui, when, s } from '@owenscorning/pcb.alpha';
import _ from 'lodash';

// eslint-disable-next-line
import itemListResponse, { generateFilterOptions } from './helpers/item_list_response';
import pathToContent from './helpers/path_to_content';
import cmsApi from '../../../../cms_api';
import { expandRef, wrapRef } from '../../../../data';
import useReference from '../../../../hooks/use_reference';
import Theme from '../../../../themes/index';
import IconDownload from '../../../ComponentLibrary/icons/icon-download';
import Button from '../../../ComponentLibrary/oc-button';
import Text from '../../../OC/PageBuilder/utilities/Text';
import RichText from '../../../OC/oc-rich-text';
import getFormatFromMime from '../../../helpers/get_format_from_mime';
import { digChoicesFromData, digItemAttributesFromData } from '../../Library/AttributeSet';

// Styled Components
const ResultItem = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  position: relative;
  width: 100%;
`;

const Container = styled.div`
  font-family: 'OCRoboto';
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  position: relative;
  width: 100%;
`;

const Heading = styled.h5`
  margin: 0;
  font-family: 'OCRoboto';
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
`;

const DocumentDetails = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
  gap: 10px;
`;

const Description = styled.p`
  margin: 0px;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
`;

const DownloadButton = styled(Button)`
  gap: 4px;
  min-width: 95px;
  width: 95px;
`;

const Thumbnail = styled.img`
  max-height: 150px;
  align-self: stretch;
  margin-right: 16px;
  object-fit: contain;
`;

const AttributeList = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  font-size: 14px;
  gap: 8px 16px;

  ${props =>
    props.splitLayout &&
    `
    @media(min-width: 768px) {
      flex-direction: row;

      > span {
        @media(min-width: 768px) {
          width: calc(50% - 8px);
        }
      }
    }
  `}
`;

const Attribute = styled.span`
  align-item: center;
  color: ${Theme.colors.textGrey};
  display: flex;
  font-size: 14px;
  position: relative;
  width: 100%;

  strong {
    margin-right: 8px;
  }
`;

export const addOriginToUrl = url => {
  if (!url) return '';

  const { origin } = window.location;
  if (url.startsWith('http')) return url;

  return `${origin}${url}`;
};

const getDocFormat = result => {
  if (!result) return '';

  let mime;
  switch (result?.contents?.type) {
    case 'dms':
      mime = result?.contents?.dms?.mime_type;
      break;
    case 'upload':
      mime = result?.contents?.document?.mime_type;
      break;
    case 'external':
      return 'external';
    default:
      return '';
  }

  return mime ? getFormatFromMime(mime) : '';
};

const getDocDescription = result => {
  if (!result) return '';

  return result.metadata?.settings?.attributes?.short_description;
};

const getDocTitle = result => {
  if (!result) return '';

  return result?.metadata?.settings?.attributes?.title || result?.name || result?.route;
};

// eslint-disable-next-line react/prop-types
export const DocBuilderResultBlock = ({ attributes, ...result }) => {
  const { dwgUrl = '' } = result;
  const title = getDocTitle(result);
  const docFormat = getDocFormat(result);
  const shortDescription = getDocDescription(result);
  const thumbnailUrl = result.contents?.thumbnail?.url;

  const infoItems = (attributes || [])
    .map(attribute => {
      const { label, field, type, of } = attribute;

      if (!label) {
        return null;
      }

      const resultValue = _.get(result, field);
      switch (type) {
        case 'choices':
          return {
            label,
            value: _.sortBy(Array.wrap(resultValue), a => {
              const keys = Object.keys(of || {});
              return keys.indexOf(a);
            }).map(v => of?.[v]?.label || v),
          };
        default:
          return { label, value: resultValue?.toString() };
      }
    })
    .filter(x => x.value?.length > 0);

  const infoItemComponents = infoItems.map(infoItem => (
    <Attribute>
      <strong>{infoItem.label}</strong> {Array.wrap(infoItem.value).join(', ')}
    </Attribute>
  ));

  return (
    <ResultItem>
      {thumbnailUrl && (
        <a
          href={pathToContent(result)}
          target="_blank"
          rel="noreferrer"
          data-track="file-open"
          data-track-doc-type="Paroc Structure Selector"
          data-track-element-location="search results"
          data-track-file-name={result.route}
        >
          <Thumbnail src={thumbnailUrl} alt={title} />
        </a>
      )}
      <Container>
        <Heading className="document-heading">
          <Text content={title} />
        </Heading>
        {result.showDescription && shortDescription && (
          <Description>
            <RichText content={shortDescription} />
          </Description>
        )}
        <AttributeList splitLayout={infoItemComponents.length > 3}>{infoItemComponents}</AttributeList>

        <DocumentDetails>
          <DownloadButton
            color="white"
            href={`${pathToContent(result)}?disposition=attachment`}
            data-track="file-download"
            data-track-doc-type="Paroc Structure Selector"
            data-track-element-location="search results"
            data-track-file-name={result.route}
          >
            <IconDownload />
            {docFormat?.toUpperCase()}
          </DownloadButton>
          {dwgUrl && (
            <DownloadButton
              color="white"
              href={`${dwgUrl}?disposition=attachment`}
              data-track="file-download"
              data-track-doc-type="Paroc Structure Selector"
              data-track-element-location="search results"
              data-track-file-name={result.route?.replace('-pdf', '-dwg')}
            >
              <IconDownload />
              DWG
            </DownloadButton>
          )}
        </DocumentDetails>
      </Container>
    </ResultItem>
  );
};

const searchFields = [
  ['metadata', 'settings', 'attributes', 'keywords'],
  ['metadata', 'settings', 'attributes', 'title'],
];

const joinPdfDwgToResults = (results = []) => {
  const dwgToFilterOut = [];
  return results
    .map(result => {
      if (result.metadata?.settings?.attributes?.['File Type'] === 'pdf') {
        const dwg =
          results.find(
            r =>
              r.metadata?.settings?.attributes?.['File Type'] === 'dwg' &&
              r.route.replace('-dwg', '') === result.route.replace('-pdf', '')
          ) || null;
        if (dwg) {
          dwgToFilterOut.push(dwg.id);
          return { ...result, dwgUrl: pathToContent(dwg) || null };
        }
        return result;
      }
      return result;
    })
    .filter(result => dwgToFilterOut.indexOf(result.id) === -1);
};

const docListItemListResponse = ({
  items,
  attributes,
  showFilters,
  enabledFilters,
  allFilters,
  showDescription,
  showEmailDocument,
}) => {
  let results = items;
  if (items && !Array.isArray(items)) {
    results = items.results;
  }
  results = joinPdfDwgToResults(results);

  return itemListResponse({
    items: results,
    filters: generateFilterOptions(showFilters, enabledFilters, allFilters, null),
    Component: props => (
      <DocBuilderResultBlock
        attributes={attributes}
        showDescription={showDescription}
        showEmailDocument={showEmailDocument}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
    ),
    searchFields,
    enableSearch: true,
  });
};

const filtersToExclude = ['metadata.settings.attributes.country', 'metadata.settings.attributes.language'];

const mapping = {
  ItemList: {
    availableIn: ['www.paroc.com'],
    name: 'Paroc Structure Selector',
    meta: async () => {
      const query = {
        filter: {
          type: 'Cms::Content::AttributeSet',
          name: 'Document Attributes',
          route: '/',
        },
        fields: {
          '*': 'contents,metadata',
        },
      };
      const data = await cmsApi.get_single_for_language(
        query,
        // eslint-disable-next-line no-undef
        Board.build.language
      );
      const hasFilters = !!data;
      const filterChoices = hasFilters ? await digChoicesFromData('metadata.settings.attributes', data) : null;
      const taxonomies = hasFilters
        ? Object.fromEntries(
            Object.entries(filterChoices)
              .filter(([key]) => filtersToExclude.indexOf(key) === -1)
              .map(([key, entry]) => [key, entry.label])
          )
        : {};
      const filters = hasFilters
        ? ui`Form`.of(
            Object.fromEntries(
              Object.entries(filterChoices).map(([key, { of, ...entry }]) => [
                key,
                ui`Choices`.of(of)({ ...entry, multiple: true, includeParentTree: false }),
              ])
            )
          )
        : null;
      const defaultEnabledFilterKeys = Object.keys(taxonomies);
      const enabledFilters = hasFilters
        ? ui`ChoicesDraggable`.of(taxonomies)({
            // ** here is where we need to add the filter UI type
            label: 'Filters',
            visible: when`../showFilters`.is.equal.to(true).then(true).otherwise(false),
            default: {
              enabledFilters: defaultEnabledFilterKeys,
              orderedList: Object.keys(taxonomies).map(k => ({ [k]: defaultEnabledFilterKeys.indexOf(k) >= 0 })),
            },
          })
        : null;
      const itemAttributeOptions = digItemAttributesFromData(data);
      const itemAttributes =
        itemAttributeOptions.length > 0
          ? ui`Choices`.of(itemAttributeOptions)({
              label: 'Item Attributes',
              sublabel: 'Select Item Attributes to Display:',
              mode: ui`Choices/Mode/Vertical`,
              multiple: true,
              tip: 'If more than three items are selected, they will shift to two columns.',
            })
          : null;

      return {
        source: ui`Choices`.of({
          all: 'All Items',
          filtered: 'Filtered',
          specific: 'Select Specific Items',
        })({
          label: 'Select Structure',
          default: 'all',
          mode: ui`Choices/Mode/Dropdown`,
        }),
        filters: ui`List/Item`.of(filters)({
          standalone: true,
          title: 'Document Attributes',
          label: 'Filters',
          visible: when`../source`.is.equal.to('filtered'),
        }),
        items: ui`List`.of({
          item: ui`Search`({
            startOpen: when`../item`.isnt.present.then(true).otherwise(false),
            label: 'Document',
            dataset: 'document_builder',
            set: value => {
              const ref = wrapRef('Cms::Content', { type: 'Document', id: value.id });
              expandRef(ref).then();
              return ref;
            },
            get: value => {
              const { results, error, loading } = useReference(value);
              if (loading) {
                return <i>Loading...</i>;
              }
              if (error) {
                return <span>{error}</span>;
              }
              return results;
            },
          }),
        })({
          singular: 'Document',
          title: 'proper_name',
          label: 'Documents',
          visible: when`../source`.is.equal.to('specific').then(true).otherwise(false),
        }),
        showFilters: ui`Switch`({
          label: 'Filter Pane',
          default: hasFilters,
          disabled: !hasFilters,
        }),
        [s._]: ui`Tip`.of(`Cannot find AttributeSet <b>Document Attributes</b> in a matching language`)({
          visible: !hasFilters,
        }),
        [s._]: ui`Tip`.of(
          // eslint-disable-next-line max-len
          'If the item list you wish to display is to be very long, we recommend turning on the Filter Side Pane to allow users to filter down the results to their needs.'
        )({
          visible: hasFilters,
        }),
        enabledFilters,
        showDescription: ui`Switch`({
          label: 'Show Item Description',
          default: false,
        }),
        showEmailDocument: ui`Switch`({
          label: 'Show Email Document Button',
          default: false,
        }),
        itemAttributes,
      };
    },
    view: (data = null) => {
      const {
        data: items,
        meta: { parameters, filters, attributes },
      } = data || { meta: {} };
      const { showFilters, enabledFilters, showDescription, showEmailDocument } = parameters || {};
      return docListItemListResponse({
        items,
        attributes,
        showFilters,
        enabledFilters,
        allFilters: filters,
        showDescription,
        showEmailDocument,
      });
    },
  },
};

export default mapping;
